// The technician's day's wire layer (PRD 95 Wave 1, §6), under
// /api/v1/business/field/:
//
//   GET   my-day?date&technician_id            My day
//   GET   visits/:id/workspace                 the visit screen
//   POST  visits/:id/events                    PRD 90's tap, with stated_time/stated_on
//   GET   clock?technician_id | POST clock/in | clock/out | clock/breaks/start | clock/breaks/end
//   GET   clock-sessions?date | POST clock-sessions | PATCH clock-sessions/:id   the office's time view
//   GET   my-time?week                         My time
//   GET   jobs/:id/labour | PATCH labor-segments/:id
//   GET   pick-list?q                          Add from price book
//   GET | PATCH technician-day-settings
//
// EVERY RESPONSE IS PARSED: apiFetch never throws on a status, so parseEnvelope
// turns 422 not_clocked_in, 409 stale and 403 module_required into a thrown
// BusinessApiError with the server's code and sentence.
import { businessApiGet, businessApiPatch, businessApiPost } from '../utils/apiUtils';
import { buildQuery, parseData, parseEnvelope } from './businessApiCommon';
import { type MaybeOffline, withFieldSnapshot } from '../components/fieldService/offline/fieldSnapshots';
import type { ISODate, UUID } from '../types/businessApi';
import type { VisitWrite } from '../types/fieldJobsApi';
import type {
  ClockEdgeParams,
  ClockSessionRow,
  ClockState,
  DayOfTime,
  DayTapParams,
  JobLabour,
  LaborSegmentRow,
  MyDay,
  MyTimeWeek,
  PickItem,
  PickListResult,
  TechnicianDaySettingsPayload,
  VisitWorkspace,
} from '../types/fieldDayApi';

const FIELD = '/api/v1/business/field';
const id = (value: string) => encodeURIComponent(value);

/** Refusals a screen answers in kind. */
export const NOT_CLOCKED_IN_CODE = 'not_clocked_in';
export const NOT_A_TECHNICIAN_CODE = 'not_a_technician';
export const ON_VISIT_CODE = 'on_visit';
export const SESSION_CLOSED_CODE = 'session_closed';

/** A v4 UUID for a press — taken when the press lands, resent unchanged on a retry. */
export function newPressId(): string {
  const cryptoApi = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const hex = (n: number) => Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${(8 + Math.floor(Math.random() * 4)).toString(16)}${hex(3)}-${hex(12)}`;
}

export const fieldDayService = {
  /**
   * A technician's own day is kept for reading with no signal (PRD 95 §4.13),
   * while the business allows it; with no signal the last copy comes back
   * with `offline_as_of`. Someone else's day is never kept.
   */
  async myDay(query: { date?: ISODate; technician_id?: UUID } = {}): Promise<MaybeOffline<MyDay>> {
    const fetchDay = async () => parseData<MyDay>(await businessApiGet(`${FIELD}/my-day${buildQuery(query)}`));
    if (query.technician_id) return fetchDay();
    return withFieldSnapshot('my-day', query.date ?? 'today', fetchDay,
      day => (day.settings.offline_snapshots === false ? 'purge' : 'keep'));
  },

  /** A visit the person works on is kept the same way. */
  async workspace(visitId: UUID): Promise<MaybeOffline<VisitWorkspace>> {
    return withFieldSnapshot(
      'workspace', visitId,
      async () => parseData<VisitWorkspace>(await businessApiGet(`${FIELD}/visits/${id(visitId)}/workspace`)),
      workspace => {
        if (workspace.settings.offline_snapshots === false) return 'purge';
        return workspace.my_assignment !== null ? 'keep' : 'skip';
      }
    );
  },

  async tap(visitId: UUID, params: DayTapParams): Promise<VisitWrite & { replayed: boolean }> {
    const { data, meta } = await parseEnvelope<VisitWrite>(await businessApiPost(`${FIELD}/visits/${id(visitId)}/events`, params));
    return { ...data, replayed: (meta as { replayed?: boolean }).replayed === true };
  },

  async clock(technicianId?: UUID): Promise<ClockState> {
    return parseData<ClockState>(await businessApiGet(`${FIELD}/clock${buildQuery({ technician_id: technicianId })}`));
  },

  async clockIn(params: ClockEdgeParams & { clock_session_id?: UUID }): Promise<ClockState> {
    return parseData<ClockState>(await businessApiPost(`${FIELD}/clock/in`, params));
  },

  async clockOut(params: ClockEdgeParams): Promise<ClockState> {
    return parseData<ClockState>(await businessApiPost(`${FIELD}/clock/out`, params));
  },

  async startBreak(): Promise<ClockState> {
    return parseData<ClockState>(await businessApiPost(`${FIELD}/clock/breaks/start`, {}));
  },

  async endBreak(): Promise<ClockState> {
    return parseData<ClockState>(await businessApiPost(`${FIELD}/clock/breaks/end`, {}));
  },

  async dayOfTime(date?: ISODate): Promise<{ day: DayOfTime; canCorrect: boolean; seesPay: boolean }> {
    const { data, meta } = await parseEnvelope<DayOfTime>(await businessApiGet(`${FIELD}/clock-sessions${buildQuery({ date })}`));
    const flags = meta as { can_correct?: boolean; sees_pay?: boolean };
    return { day: data, canCorrect: flags.can_correct === true, seesPay: flags.sees_pay === true };
  },

  async recordSession(params: { technician_id: UUID; started_at: string; ended_at?: string; reason: string }): Promise<ClockSessionRow> {
    return parseData<ClockSessionRow>(await businessApiPost(`${FIELD}/clock-sessions`, params));
  },

  async correctSession(
    sessionId: UUID,
    params: { started_at?: string; ended_at?: string; reason: string; lock_version: number }
  ): Promise<ClockSessionRow> {
    return parseData<ClockSessionRow>(await businessApiPatch(`${FIELD}/clock-sessions/${id(sessionId)}`, params));
  },

  async myTime(week?: ISODate, technicianId?: UUID): Promise<MyTimeWeek> {
    return parseData<MyTimeWeek>(await businessApiGet(`${FIELD}/my-time${buildQuery({ week, technician_id: technicianId })}`));
  },

  async jobLabour(jobId: UUID): Promise<JobLabour> {
    return parseData<JobLabour>(await businessApiGet(`${FIELD}/jobs/${id(jobId)}/labour`));
  },

  async updateSegment(segmentId: UUID, params: { billable?: boolean; note?: string }): Promise<LaborSegmentRow> {
    return parseData<LaborSegmentRow>(await businessApiPatch(`${FIELD}/labor-segments/${id(segmentId)}`, params));
  },

  /**
   * `job_id` prices each row for that job's customer (PRD 97's PriceFor):
   * member and add-on prices; `category_id` narrows to one category.
   */
  async pickList(q?: string, options: { job_id?: UUID; category_id?: UUID } = {}): Promise<PickListResult> {
    const { data, meta } = await parseEnvelope<PickItem[]>(
      await businessApiGet(`${FIELD}/pick-list${buildQuery({ q, job_id: options.job_id, category_id: options.category_id })}`)
    );
    const said = meta as { prices_hidden?: boolean; price_book?: boolean };
    return { items: data ?? [], pricesHidden: said.prices_hidden === true, priceBook: said.price_book === true };
  },

  async settings(): Promise<TechnicianDaySettingsPayload> {
    return parseData<TechnicianDaySettingsPayload>(await businessApiGet(`${FIELD}/technician-day-settings`));
  },

  async saveSettings(changes: Record<string, unknown>, lockVersion: number): Promise<TechnicianDaySettingsPayload> {
    return parseData<TechnicianDaySettingsPayload>(
      await businessApiPatch(`${FIELD}/technician-day-settings`, { ...changes, lock_version: lockVersion })
    );
  },
};
